// «كلّمنا»: night photo, a large glass card, the heading on one side and the form on
// the other. REFERENCE.md §15 CTA band, spec section 11.
//
// The logic is Pages/Home/Contact.js: submitContactMessage (POST /api/public/contact
// → platform_leads, read under «محتوى الصفحة الرئيسية → رسائل الزوار»), the same
// fields, validate(), the sent state and its focus handling, and the `website`
// honeypot: hidden from people, and the server accepts a filled one silently and
// stores nothing.
//
// One addition: when the server rejects a field (the phone format is checked only
// there) its answer names the field, so the error lands on that field.

import { useEffect, useId, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, Mail, Phone } from "lucide-react";
import Words from "../Home/motion/Words";
import { submitContactMessage } from "../../services/homeContentApi";
import { telHref } from "./data";
import { Photo } from "./media";
import "./ContactBand.css";

const EMPTY = { name: "", phone: "", email: "", company: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERROR_ID = "ld-contact-error";

// [field, message] for the first problem, so that field can be marked and focused;
// null when the form can go.
const validate = (form) => {
    if (form.name.trim().length < 2) return ["name", "اكتب اسمك"];
    if (!form.phone.trim() && !form.email.trim()) return ["phone", "اكتب رقم موبايل أو بريد إلكتروني عشان نرد عليك"];
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) return ["email", "البريد الإلكتروني غير صحيح"];
    if (form.message.trim().length < 5) return ["message", "اكتب رسالتك"];
    return null;
};

export default function ContactBand({ contact }) {
    const uid = useId();
    const formRef = useRef(null);
    const doneRef = useRef(null);
    const refocus = useRef("");
    const [form, setForm] = useState(EMPTY);
    // idle | sending | sent | error
    const [status, setStatus] = useState({ state: "idle", text: "", field: "" });
    // The confirmation takes the form's height, so the page does not jump under the reader.
    const [formHeight, setFormHeight] = useState(0);

    // The form and the "sent" view replace each other, so focus would fall back to
    // the page. Send it where the visitor is: the confirmation after sending, the
    // first field after «إرسال رسالة أخرى».
    useEffect(() => {
        if (refocus.current === "done") doneRef.current?.focus();
        if (refocus.current === "form") formRef.current?.elements.name?.focus();
        refocus.current = "";
    }, [status.state]);

    const onChange = (event) => {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: value }));
        if (status.state === "error") setStatus({ state: "idle", text: "", field: "" });
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (status.state === "sending") return;
        const problem = validate(form);
        if (problem) {
            const [field, text] = problem;
            setStatus({ state: "error", text, field });
            formRef.current?.elements[field]?.focus();
            return;
        }
        setStatus({ state: "sending", text: "", field: "" });
        try {
            const res = await submitContactMessage({
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                company: form.company.trim(),
                message: form.message.trim(),
                website: form.website,
            });
            setFormHeight(formRef.current?.offsetHeight || 0);
            setForm(EMPTY);
            refocus.current = "done";
            setStatus({ state: "sent", text: res?.message || "تم استلام رسالتك، هنتواصل معاك قريب.", field: "" });
        } catch (err) {
            const field = err?.response?.data?.field || "";
            setStatus({
                state: "error",
                text: err?.response?.data?.message || "تعذّر إرسال رسالتك الآن — حاول مرة أخرى بعد قليل.",
                field,
            });
            if (field) formRef.current?.elements[field]?.focus();
        }
    };

    const again = () => {
        refocus.current = "form";
        setStatus({ state: "idle", text: "", field: "" });
    };

    const sending = status.state === "sending";
    const sent = status.state === "sent";
    const fieldError = status.state === "error" && status.field ? status.field : "";

    // One floating-label field. The label comes after the control so CSS can float
    // it; the placeholder shows only while the field has focus.
    const renderField = ({ name, label, required = false, area = false, wide = false, ...control }) => {
        const id = `${uid}-${name}`;
        const invalid = fieldError === name;
        const Control = area ? "textarea" : "input";
        return (
            <div className={`ld-field ${area ? "ld-field--area" : ""} ${wide ? "ld-field--wide" : ""}`.trim()}>
                <Control
                    id={id}
                    className="ld-field__control"
                    name={name}
                    value={form[name]}
                    onChange={onChange}
                    required={required || undefined}
                    aria-invalid={invalid ? true : undefined}
                    aria-describedby={invalid ? ERROR_ID : undefined}
                    {...control}
                />
                <label className="ld-field__label" htmlFor={id}>
                    {label}
                    {required ? <span aria-hidden="true"> *</span> : null}
                </label>
                {invalid ? <p className="ld-field__error" id={ERROR_ID} role="alert">{status.text}</p> : null}
            </div>
        );
    };

    const channels = [
        contact.phone && {
            key: "phone",
            Icon: Phone,
            label: contact.phoneLabel || "الهاتف وواتساب",
            value: contact.phone,
            href: telHref(contact.phone),
        },
        contact.email && {
            key: "email",
            Icon: Mail,
            label: contact.emailLabel || "البريد الإلكتروني",
            value: contact.email,
            href: `mailto:${contact.email}`,
        },
    ].filter(Boolean);

    return (
        <section className="ld-section ld-section--night ld-contact" id="contact" aria-labelledby="ld-contact-title">
            <div className="ld-decor" aria-hidden="true">
                <Photo name="rooftop" style={{ "--ld-focal": "50% 100%" }} />
                <div className="ld-contact__veil" />
                <div className="ld-grain" />
            </div>

            <div className="ld-container">
                <div className="ld-contact__panel ld-glass-night" data-ld="scale">
                    <div className="ld-contact__intro">
                        {contact.eyebrow ? (
                            <span className="ld-eyebrow" data-ld="rise" style={{ "--ld-d": ".15s" }}>{contact.eyebrow}</span>
                        ) : null}
                        <h2 className="ld-h2" id="ld-contact-title" data-ld="words" style={{ "--ld-d": ".2s" }}>
                            <Words text={`${contact.titleAccent}${contact.titleRest}`} wordClass="ld-word" />
                        </h2>
                        <span className="ld-rule ld-contact__rule" data-ld="rule" style={{ "--ld-d": ".4s" }} />
                        <p className="ld-lead ld-contact__line" data-ld="rise" style={{ "--ld-d": ".45s" }}>{contact.subtitle}</p>
                        {channels.length ? (
                            <ul className="ld-contact__channels" data-ld="group" style={{ "--ld-d": ".55s" }}>
                                {channels.map(({ key, Icon, label, value, href }) => (
                                    <li key={key}>
                                        <a className="ld-contact__channel" href={href} aria-label={`${label}: ${value}`}>
                                            <Icon aria-hidden="true" />
                                            <span dir="ltr">{value}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>

                    <div className="ld-contact__side">
                        {/* Present before anything changes, so the confirmation is read out. */}
                        <p className="ld-sr" role="status">{sent ? status.text : ""}</p>

                        {sent ? (
                            <div
                                className="ld-contact__done"
                                ref={doneRef}
                                tabIndex={-1}
                                style={formHeight ? { minHeight: formHeight } : undefined}
                            >
                                <span className="ld-disc" aria-hidden="true"><Check /></span>
                                <strong className="ld-contact__done-text">{status.text}</strong>
                                <button type="button" className="ld-btn ld-btn--secondary" onClick={again}>
                                    إرسال رسالة أخرى
                                </button>
                            </div>
                        ) : (
                            <form ref={formRef} className="ld-contact__form" onSubmit={onSubmit} noValidate>
                                <div className="ld-contact__fields" data-ld="fields" style={{ "--ld-d": ".55s" }}>
                                    {renderField({ name: "name", label: "الاسم", required: true, type: "text", placeholder: "اسمك الكامل", autoComplete: "name", maxLength: 120 })}
                                    {renderField({ name: "phone", label: "الموبايل / واتساب", type: "tel", dir: "ltr", placeholder: "01xxxxxxxxx", autoComplete: "tel", maxLength: 40 })}
                                    {renderField({ name: "email", label: "البريد الإلكتروني", type: "email", dir: "ltr", placeholder: "you@company.com", autoComplete: "email", maxLength: 190 })}
                                    {renderField({ name: "company", label: "اسم الشركة (اختياري)", type: "text", placeholder: "شركة التكييف", autoComplete: "organization", maxLength: 160 })}
                                    {renderField({ name: "message", label: "الرسالة", required: true, area: true, wide: true, placeholder: "اكتب رسالتك...", rows: 3, maxLength: 4000 })}
                                </div>

                                {/* Honeypot: not for people. */}
                                <label className="ld-sr" aria-hidden="true">
                                    <span>Website</span>
                                    <input type="text" name="website" value={form.website} onChange={onChange} tabIndex={-1} autoComplete="off" />
                                </label>

                                {status.state === "error" && !status.field ? (
                                    <p className="ld-contact__alert" id={ERROR_ID} role="alert">
                                        <AlertCircle aria-hidden="true" />
                                        <span>{status.text}</span>
                                    </p>
                                ) : null}

                                <div data-ld="fields" style={{ "--ld-d": ".95s" }}>
                                    <button type="submit" className="ld-btn ld-btn--primary ld-btn--lg ld-btn--block" disabled={sending}>
                                        {sending ? <Loader2 className="ld-contact__spin" aria-hidden="true" /> : null}
                                        {sending ? "جارٍ الإرسال…" : "ابدأ المحادثة"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
